// Drawer displaying execution call logs for an Outbound HTTP API (WP-13).
import { useState, useEffect, useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  getHttpApiCallLogs,
  type HttpApiDoc,
  type HttpApiCallDoc,
} from '../../lib/httpApi';

interface HttpApiCallLogDrawerProps {
  open: boolean;
  onClose: () => void;
  api: HttpApiDoc | null;
}

export function HttpApiCallLogDrawer({ open, onClose, api }: HttpApiCallLogDrawerProps) {
  const { t } = useI18n();

  const [logs, setLogs] = useState<HttpApiCallDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(open && !!api, panelRef, onClose);

  const fetchLogs = async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getHttpApiCallLogs(api.id, 50);
      setLogs(data);
    } catch (err: any) {
      setError(err.message || t('httpApi.logs.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && api) {
      fetchLogs();
    } else {
      setLogs([]);
      setExpandedCallId(null);
    }
  }, [open, api]);

  if (!open || !api) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col dark:bg-slate-900 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>{t('httpApi.logs.drawerTitle')}</span>
              <Badge variant="brand">{api.name}</Badge>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('httpApi.logs.drawerSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchLogs}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('common.refresh')}
            </Button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Aggregate Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-4 bg-slate-50 border-b border-slate-200 dark:bg-slate-900/40 dark:border-slate-800 text-center">
          <div className="p-2 rounded bg-white dark:bg-slate-800 shadow-sm">
            <span className="text-[11px] text-slate-500 block">{t('httpApi.logs.totalCalls')}</span>
            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
              {api.stats?.totalCalls || 0}
            </span>
          </div>
          <div className="p-2 rounded bg-white dark:bg-slate-800 shadow-sm">
            <span className="text-[11px] text-emerald-600 block">{t('httpApi.logs.success')}</span>
            <span className="text-sm font-bold text-emerald-600">
              {api.stats?.success || 0}
            </span>
          </div>
          <div className="p-2 rounded bg-white dark:bg-slate-800 shadow-sm">
            <span className="text-[11px] text-rose-600 block">{t('httpApi.logs.errors')}</span>
            <span className="text-sm font-bold text-rose-600">
              {api.stats?.error || 0}
            </span>
          </div>
        </div>

        {/* Logs List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          {loading && logs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              {t('common.loading')}
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              {t('httpApi.logs.noLogs')}
            </div>
          ) : (
            logs.map((log) => {
              const isExpanded = expandedCallId === log.id;
              const isOk = log.responseStatus >= 200 && log.responseStatus < 300;

              return (
                <div
                  key={log.id}
                  className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                >
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedCallId(isExpanded ? null : log.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant={isOk ? 'success' : 'danger'}>
                        {log.responseStatus || 'ERR'}
                      </Badge>
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                        {log.method}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        {log.durationMs}ms
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">
                        {new Date(log.at).toLocaleString()}
                      </span>
                      <span className="text-xs text-slate-400">
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs font-mono text-slate-600 dark:text-slate-400 mt-1 truncate">
                    {log.url}
                  </p>

                  {/* Expandable inspector */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      {log.requestBodySnippet && (
                        <div>
                          <label className="text-[10px] font-semibold uppercase text-slate-400 block mb-1">
                            {t('httpApi.logs.requestBody')}
                          </label>
                          <pre className="text-[11px] font-mono bg-slate-50 p-2 rounded border border-slate-200 text-slate-700 dark:bg-slate-950 dark:border-slate-800 dark:text-slate-300 max-h-36 overflow-auto">
                            {log.requestBodySnippet}
                          </pre>
                        </div>
                      )}

                      {log.responseBodySnippet && (
                        <div>
                          <label className="text-[10px] font-semibold uppercase text-slate-400 block mb-1">
                            {t('httpApi.logs.responseBody')}
                          </label>
                          <pre className="text-[11px] font-mono bg-slate-950 p-2 rounded text-emerald-400 max-h-36 overflow-auto">
                            {log.responseBodySnippet}
                          </pre>
                        </div>
                      )}

                      {log.error && (
                        <div>
                          <label className="text-[10px] font-semibold uppercase text-rose-500 block mb-1">
                            {t('httpApi.logs.errorDetail')}
                          </label>
                          <p className="text-xs text-rose-600 bg-rose-50 p-2 rounded dark:bg-rose-950/40 dark:text-rose-300">
                            {log.error}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
